/** Profile photos upload as a square JPEG this many pixels wide, or smaller when the original is. */
export const PROFILE_PHOTO_PIXELS = 512

/** The centred square that crops a width × height image. */
export function centeredSquare(width: number, height: number) {
  const side = Math.min(width, height)
  return { x: (width - side) / 2, y: (height - side) / 2, side }
}

export class UnreadablePhotoError extends Error {
  constructor() {
    super('This file could not be read as a photo.')
    this.name = 'UnreadablePhotoError'
  }
}

type DecodedPhoto = { source: CanvasImageSource; width: number; height: number; release: () => void }

async function decodePhoto(file: Blob): Promise<DecodedPhoto> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() }
    } catch {
      // Fall back to an image element, which decodes more formats in some browsers (HEIC in Safari).
    }
  }
  const url = URL.createObjectURL(file)
  const image = new Image()
  image.src = url
  try {
    await image.decode()
  } catch {
    URL.revokeObjectURL(url)
    throw new UnreadablePhotoError()
  }
  return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => URL.revokeObjectURL(url) }
}

/**
 * Crops a picked image to its centred square and scales it down to a JPEG, so phone photos upload quickly in any
 * format the browser can open. Rejects with UnreadablePhotoError when the browser can't decode the file.
 */
export async function prepareProfilePhoto(file: Blob): Promise<Blob> {
  const photo = await decodePhoto(file)
  try {
    if (!photo.width || !photo.height) throw new UnreadablePhotoError()
    const crop = centeredSquare(photo.width, photo.height)
    const size = Math.min(PROFILE_PHOTO_PIXELS, Math.round(crop.side))
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d')
    if (!context) throw new UnreadablePhotoError()
    // JPEG has no transparency, so transparent PNGs sit on white rather than black.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, size, size)
    context.drawImage(photo.source, crop.x, crop.y, crop.side, crop.side, 0, 0, size, size)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) throw new UnreadablePhotoError()
    return blob
  } finally {
    photo.release()
  }
}
